import React, { useEffect, useMemo, useState } from 'react';
import {
  Grid,
  Typography,
  Card,
  CardContent,
  RACTable,
  RACTableCell,
  RACTableRow,
  RACButton,
  RACCheckBox,
  CircularProgress,
} from '@rentacenter/racstrap';
import { customerDetailsStyle } from './../customerStyles';
import { ReactComponent as NoRecordsFound } from '../../../assets/images/No-records.svg';
import CollapseArrow from '../../../assets/images/Accordion-close-updated.png';
import ExpandArrow from '../../../assets/images/Accordion-down-updated.png';
import { ReactComponent as AlertIcon } from '../../../emilyIcons/no-records-found.svg';
import { Agreement } from '../../../constants/customerDetailsInterface';
import {
  generateAccountReport,
  getAgreementsWithReportData,
} from '../../../utils/AccountReportUtils';
import type {
  AgreementApiResponse,
  SelectedAgreement,
} from '../../../utils/AccountReportUtils';

interface AccountReportTabProps {
  activeAgreements: Agreement[];
  inactiveAgreements: Agreement[];
  activeAgrStatus: number;
  inActiveAgrStatus: number;
  customerId: string | number;
  cardLoader: boolean;
  onGenerateReport?: (payload: {
    selected: SelectedAgreement[];
  }) => Promise<void>;
  activeApiResponse?: AgreementApiResponse;
  inactiveApiResponse?: AgreementApiResponse;
}

const LOADER_OVERLAY_STYLE: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(255, 255, 255, 0.7)',
  zIndex: 100,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
};

const FOOTER_BAR_STYLE: React.CSSProperties = {
  position: 'sticky',
  top: 0,
  backgroundColor: 'white',
  zIndex: 10,
  padding: '10px',
  borderTop: '1px solid #e0e0e0',
};

const ERROR_MESSAGE_STYLE: React.CSSProperties = {
  color: '#d32f2f',
  fontSize: '14px',
  textAlign: 'right',
  padding: '8px',
  backgroundColor: '#ffebee',
  borderRadius: '4px',
};

/**
 * Derive renter label from an agreement's first customer priority.
 */
const getRenterLabel = (obj: Agreement): string => {
  const priority = obj.customers?.[0]?.priority;
  if (!priority) return '-';
  return String(priority) === '1' ? 'Renter' : 'Co-Renter';
};

/**
 * Truncate a description to a max visible length.
 */
const truncateDescription = (desc?: string | null, maxLen = 15): string => {
  if (!desc) return '-';
  return desc.length > maxLen ? desc.slice(0, maxLen - 2) + '...' : desc;
};

export default function AccountReportTab(props: AccountReportTabProps) {
  const classes = customerDetailsStyle();

  const [selectedMap, setSelectedMap] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState(false);
  const [showActive, setShowActive] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  // Get the set of agreement IDs/numbers that have accountReportResponse data
  const {
    agreementIds: reportAgreementIds,
    agreementNumbers: reportAgreementNumbers,
  } = getAgreementsWithReportData(
    props.activeApiResponse,
    props.inactiveApiResponse
  );

  const hasReportData = (agr: Agreement): boolean => {
    const id = String(agr.agreementId ?? '');
    const num = agr.agreementNumber ?? '';
    return reportAgreementIds.has(id) || reportAgreementNumbers.has(num);
  };

  // Filter agreements to only show those that have account report data
  const active: Agreement[] = useMemo(
    () => props.activeAgreements.filter(hasReportData),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.activeAgreements, props.activeApiResponse, props.inactiveApiResponse]
  );
  const inactive: Agreement[] = useMemo(
    () => props.inactiveAgreements.filter(hasReportData),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      props.inactiveAgreements,
      props.activeApiResponse,
      props.inactiveApiResponse,
    ]
  );

  useEffect(() => {
    // reset selections when data or API responses change
    setSelectedMap({});
    setErrorMessage('');
  }, [
    props.activeAgreements,
    props.inactiveAgreements,
    props.activeApiResponse,
    props.inactiveApiResponse,
  ]);

  const getKeyFor = (r: Agreement) => {
    return String(r.agreementId ?? r.agreementNumber ?? '');
  };

  const getSelectedItems = () => {
    const allRows = [...active, ...inactive];
    return allRows
      .filter((r) => {
        const key = getKeyFor(r);
        return key && selectedMap[key];
      })
      .map((r) => ({
        agreementNumber: r.agreementNumber || '',
        agreementId: r.agreementId || 0,
      }));
  };

  const toggleSelect = (row: Agreement) => {
    const k = getKeyFor(row);
    if (!k) return;
    setSelectedMap((prev) => ({
      ...prev,
      [k]: !prev[k],
    }));
  };

  const toggleSelectAll = () => {
    const allData = [...active, ...inactive];
    const allSelected = isAllSelected(allData);
    const updates: Record<string, boolean> = {};

    allData.forEach((row) => {
      const k = getKeyFor(row);
      if (k) {
        updates[k] = !allSelected;
      }
    });

    setSelectedMap(updates);
  };

  const isAllSelected = (data: Agreement[]) => {
    if (!data || data.length === 0) return false;
    return data.every((row) => {
      const k = getKeyFor(row);
      return k && selectedMap[k];
    });
  };

  const isSomeSelected = (data: Agreement[]) => {
    if (!data || data.length === 0) return false;
    return data.some((row) => {
      const k = getKeyFor(row);
      return k && selectedMap[k];
    });
  };

  const handleGenerate = async () => {
    const selectedItems = getSelectedItems();

    // Validation
    if (selectedItems.length === 0) {
      setErrorMessage(
        'Please select at least one agreement to generate the report.'
      );
      return;
    }

    if (submitting) {
      return;
    }

    // Clear any previous error messages
    setErrorMessage('');
    setSubmitting(true);

    await new Promise<void>((resolve) =>
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
    );

    try {
      // Generate the account report Excel file with API responses from props
      const result = await generateAccountReport(
        props.customerId,
        selectedItems,
        props.activeApiResponse,
        props.inactiveApiResponse
      );

      // Show partial-failure summary if any agreements were skipped.
      if (result && result.failed.length > 0) {
        const skipped = result.failed
          .map((f) => f.agreement.agreementNumber || f.agreement.agreementId)
          .join(', ');
        setErrorMessage(
          `Generated ${result.succeeded.length} of ${result.requested} agreement(s). ` +
            `${result.failed.length} could not be included: ${skipped}.`
        );
      }

      // Optional: Call the parent callback if provided
      if (props.onGenerateReport) {
        const payload = { selected: selectedItems };
        await props.onGenerateReport(payload);
      }
    } catch (error: unknown) {
      const errorMsg =
        error instanceof Error
          ? error.message
          : 'An error occurred while generating the report. Please try again.';
      setErrorMessage(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const renderSectionHeader = (
    expanded: boolean,
    toggle: () => void,
    title: string
  ) => (
    <RACTableRow
      style={{ backgroundColor: 'white' }}
      className={`${classes.fixTableHeadAgrTrsRec1} ${classes.semiBold}`}
    >
      <RACTableCell style={{ width: '3%' }}>
        {expanded ? (
          <img
            src={ExpandArrow}
            style={{ width: '60%' }}
            className={`${classes.arrowIcon} ${classes.pointer}`}
            onClick={toggle}
          />
        ) : (
          <img
            src={CollapseArrow}
            style={{ width: '60%' }}
            className={`${classes.arrowIcon} ${classes.pointer}`}
            onClick={toggle}
          />
        )}
      </RACTableCell>
      <RACTableCell colSpan={14}>{title}</RACTableCell>
    </RACTableRow>
  );

  const renderDataRow = (obj: Agreement, index: number, title: string) => {
    const renter = getRenterLabel(obj);

    return (
      <RACTableRow
        key={`${title}-${index}`}
        style={{ backgroundColor: 'white' }}
      >
        <RACTableCell className={classes.textCenter}>
          <RACCheckBox
            checked={!!selectedMap[getKeyFor(obj)]}
            onChange={() => toggleSelect(obj)}
            aria-label={`select-${obj.agreementNumber}`}
          />
        </RACTableCell>
        <RACTableCell
          className={`${classes.semiBold} ${classes.textDarkSlateBlue}`}
        >
          {obj.agreementNumber || '-'}
        </RACTableCell>
        <RACTableCell className={`${classes.semiBold} ${classes.textRight}`}>
          {obj.storeNumber || '-'}
        </RACTableCell>
        <RACTableCell className={`${classes.semiBold} ${classes.textRight}`}>
          {obj.route || '-'}
        </RACTableCell>
        <RACTableCell className={classes.semiBold}>{renter}</RACTableCell>
        <RACTableCell
          className={classes.semiBold}
          title={obj.agreementDescription || '-'}
        >
          {truncateDescription(obj.agreementDescription)}
        </RACTableCell>
        <RACTableCell className={classes.semiBold}>
          {obj.agreementOpenDate || '-'}
        </RACTableCell>
        <RACTableCell className={classes.semiBold}>
          {obj.currentDueDate || '-'}
        </RACTableCell>
        <RACTableCell className={`${classes.semiBold} ${classes.textRight}`}>
          {obj.currentDayslate || '-'}
        </RACTableCell>
        <RACTableCell className={`${classes.semiBold} ${classes.textRight}`}>
          {obj.actualRate || '-'}
        </RACTableCell>
        <RACTableCell className={`${classes.semiBold} ${classes.textRight}`}>
          {obj.actualTerm || '-'}
        </RACTableCell>
        <RACTableCell className={`${classes.semiBold} ${classes.textRight}`}>
          {obj.rentPaid || '-'}
        </RACTableCell>
        <RACTableCell className={classes.semiBold}>
          {obj.closeDate || '-'}
        </RACTableCell>
        <RACTableCell className={classes.semiBold}>
          {obj.agreementStatusReason || '-'}
        </RACTableCell>
      </RACTableRow>
    );
  };

  const renderNoDataRow = (status: number, filteredEmpty: boolean) => (
    <RACTableRow>
      <RACTableCell></RACTableCell>
      <RACTableCell colSpan={14}>
        <Grid container md={12}>
          <Grid item md={12} className={`${classes.textCenter} ${classes.mt1}`}>
            {status === 204 || filteredEmpty ? (
              <NoRecordsFound />
            ) : (
              <AlertIcon />
            )}
            <Typography className={`${classes.subTitle} ${classes.mb3}`}>
              {status === 204 || filteredEmpty
                ? 'No Records Found'
                : 'Something Went Wrong'}
            </Typography>
          </Grid>
        </Grid>
      </RACTableCell>
    </RACTableRow>
  );

  const renderSectionBody = (
    title: string,
    data: Agreement[],
    originalData: Agreement[],
    status: number,
    expanded: boolean
  ) => {
    if (!expanded) return null;
    if (data.length > 0) {
      return data.map((obj: Agreement, index: number) =>
        renderDataRow(obj, index, title)
      );
    }
    const isFilteredEmpty =
      status === 200 && (originalData.length > 0 || data.length === 0);
    return renderNoDataRow(status, isFilteredEmpty);
  };

  const renderSection = (
    title: string,
    data: Agreement[],
    originalData: Agreement[],
    status: number,
    expanded: boolean,
    toggle: () => void
  ) => (
    <>
      {renderSectionHeader(expanded, toggle, title)}
      {renderSectionBody(title, data, originalData, status, expanded)}
    </>
  );

  const tableHead = () => (
    <>
      <RACTableCell className={`${classes.bold} ${classes.textCenter}`}>
        Select
      </RACTableCell>
      <RACTableCell className={classes.bold}>Agreement #</RACTableCell>
      <RACTableCell className={`${classes.bold} ${classes.textRight}`}>
        Store #
      </RACTableCell>
      <RACTableCell className={`${classes.bold} ${classes.textRight}`}>
        Route
      </RACTableCell>
      <RACTableCell className={classes.bold}>Renter</RACTableCell>
      <RACTableCell className={classes.bold}>Description</RACTableCell>
      <RACTableCell className={classes.bold}>Opened</RACTableCell>
      <RACTableCell className={classes.bold}>Due Date</RACTableCell>
      <RACTableCell className={`${classes.bold} ${classes.textRight}`}>
        Days Late
      </RACTableCell>
      <RACTableCell className={`${classes.bold} ${classes.textRight}`}>
        Rate
      </RACTableCell>
      <RACTableCell className={`${classes.bold} ${classes.textRight}`}>
        Term
      </RACTableCell>
      <RACTableCell className={`${classes.bold} ${classes.textRight}`}>
        Rent Paid
      </RACTableCell>
      <RACTableCell className={classes.bold}>Closed Date</RACTableCell>
      <RACTableCell className={classes.bold}>Closed Reason</RACTableCell>
    </>
  );

  const tableBody = () => (
    <>
      {renderSection(
        'Active',
        active,
        props.activeAgreements,
        props.activeAgrStatus,
        showActive,
        () => {
          setShowActive((prev) => {
            const newValue = !prev;
            if (newValue) {
              setShowInactive(false);
            }
            return newValue;
          });
        }
      )}
      {renderSection(
        'Inactive',
        inactive,
        props.inactiveAgreements,
        props.inActiveAgrStatus,
        showInactive,
        () => {
          setShowInactive((prev) => {
            const newValue = !prev;
            if (newValue) {
              setShowActive(false);
            }
            return newValue;
          });
        }
      )}
    </>
  );

  const selectedItemsCount = getSelectedItems().length;

  return (
    <Grid
      container
      md={12}
      className={`${classes.lightGrayBgColor} ${classes.p01} ${classes.fixTableHeadAgrTrsRec1} ${classes.semiBold} ${classes.pb70px}`}
      style={{ position: 'relative' }}
    >
      {/* Loader overlay while generating report */}
      {submitting && (
        <div style={LOADER_OVERLAY_STYLE}>
          <CircularProgress />
          <Typography style={{ marginTop: '16px', fontWeight: 600 }}>
            Generating Report...
          </Typography>
        </div>
      )}
      <Card
        className={`${classes.tabsCardWithBottom} ${classes.mt3} ${classes.hideScrollbar}`}
      >
        <CardContent>
          {props.cardLoader ? (
            <Grid className={classes.cardLoader}>
              {/* Loader could be used here from common components if desired */}
            </Grid>
          ) : (
            <Grid container md={12}>
              {/* Table */}
              <Grid item md={12}>
                <RACTable
                  className={classes.fixTableHeadAgrTrsRec1}
                  renderTableHead={tableHead}
                  renderTableContent={tableBody}
                  stickyHeader={true}
                />
              </Grid>
              <Grid
                container
                md={12}
                className={`${classes.mt2}`}
                style={FOOTER_BAR_STYLE}
              >
                <Grid
                  item
                  md={12}
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                  }}
                >
                  <RACCheckBox
                    checked={isAllSelected([...active, ...inactive])}
                    indeterminate={
                      !isAllSelected([...active, ...inactive]) &&
                      isSomeSelected([...active, ...inactive])
                    }
                    onChange={toggleSelectAll}
                    aria-label="select-all-agreements"
                  />
                  <Typography
                    style={{ marginRight: '16px', marginLeft: '8px' }}
                  >
                    Select All ({selectedItemsCount} selected)
                  </Typography>
                  <RACButton
                    disabled={selectedItemsCount === 0 || submitting}
                    onClick={handleGenerate}
                    variant="contained"
                    color="primary"
                    style={{ padding: '7px 30px' }}
                  >
                    {submitting ? 'Generating…' : 'Generate Report'}
                  </RACButton>
                </Grid>
                {errorMessage && (
                  <Grid item md={12} style={{ marginTop: '10px' }}>
                    <Typography style={ERROR_MESSAGE_STYLE}>
                      {errorMessage}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>
    </Grid>
  );
}
